import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Checkbox } from '#src/atoms/inputs/Checkbox/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineXL, LabelM } from '#src/atoms/typography/index.tsx';
import { Box } from '#src/atoms/ui/Box/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useStoreUrl } from '#src/hooks/useStoreUrl.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { glossary } from '#src/lib/glossary.ts';

import illustration from './assets/illustration.webp';

const Root = styled(Vertical)`
  padding: 0 var(--double-unit);
  height: 100%;
`;
const StyledImg = styled.img`
  height: 280px;
  object-fit: cover;
  object-position: left top;
`;
const StyledBox = styled(Box)`
  background-color: var(--c-nd-100);
`;
const StyledList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;
const StyledListItem = styled.li`
  padding: var(--unit);

  &:not(:first-child) {
    margin-top: var(--double-unit);
  }
`;
const LabelWrapper = styled.span`
  color: var(--c-nd-600);
`;
const ButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--unit);
`;
const ImgWrapper = styled(Vertical)`
  flex: 1;
  margin-left: calc(-1 * var(--double-unit));
  margin-right: calc(-1 * var(--double-unit));
  justify-content: center;
`;

type Benefit = {
  id: string;
  icon: ReactNode;
  label: MessageDescriptor;
};

type Props = {
  open: boolean;
  benefits: readonly Readonly<Benefit>[];
  onClose: (doNotShowAgain: boolean) => void;
};

export const DownloadApp = ({ open, onClose, benefits }: Props) => {
  const [doNotShowAgain, toggleDoNotShowAgain] = useToggle(false);
  const storeUrl = useStoreUrl();

  return (
    <Dialog
      open={open}
      onClose={() => onClose(doNotShowAgain)}
      fullWidth
      maxWidth="xs"
      footer={
        <Vertical gap={2}>
          <Checkbox
            checked={doNotShowAgain}
            onChange={toggleDoNotShowAgain}
            label={
              <LabelWrapper>
                <FormattedMessage
                  id="SetReminderDownloadApp.doNotShowAgain"
                  defaultMessage="Don't show this message again"
                />
              </LabelWrapper>
            }
          />
          <ButtonsWrapper>
            <Button
              color="tertiary"
              size="medium"
              onClick={() => onClose(doNotShowAgain)}
            >
              <FormattedMessage {...glossary.close} />
            </Button>
            <Button href={storeUrl} externalLink color="primary" size="medium">
              <FormattedMessage
                id="SetReminderDownloadApp.downloadCta"
                defaultMessage="Download the App"
              />
            </Button>
          </ButtonsWrapper>
        </Vertical>
      }
    >
      <Root gap={2.5}>
        <Vertical>
          <HeadlineXL as="h2">
            <FormattedMessage
              id="SetReminderDownloadApp.title"
              defaultMessage="Never miss a beat"
            />
          </HeadlineXL>
          <BodyM as="p">
            <FormattedMessage
              id="SetReminderDownloadApp.subtitle"
              defaultMessage="Download the app to get exclusive notifications"
            />
          </BodyM>
        </Vertical>
        <StyledBox>
          <StyledList>
            {benefits.map(({ id, icon, label }) => (
              <StyledListItem key={id}>
                <Horizontal gap={2}>
                  {icon}
                  <LabelM as="span">
                    <FormattedMessage {...label} />
                  </LabelM>
                </Horizontal>
              </StyledListItem>
            ))}
          </StyledList>
        </StyledBox>
        <ImgWrapper>
          <StyledImg src={illustration} alt="" />
        </ImgWrapper>
      </Root>
    </Dialog>
  );
};
